import { supabase } from "@/lib/supabase";
import { rateLimit } from "@/lib/utils/rateLimiter";
import useImageModelStore from "@/store/imageModelStore";
import { ChatCompletionService } from "../../../services/openai/chatCompletionService.js";
import { BaseTool } from "./BaseTool.js";

// Create service instance
const chatCompletionService = new ChatCompletionService();

// Upload image to Supabase storage with retry logic
const uploadImageToStorage = async (
  base64Data,
  prompt,
  maxRetries = 3,
  retryDelay = 1000
) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Get user ID from auth store
      const { useAuthStore } = await import("@/store/authStore");
      const userId =
        useAuthStore.getState().userId || useAuthStore.getState().user?.id;

      if (!userId) {
        console.error("No user ID available for image upload");
        throw new Error("User not authenticated");
      }

      // Validate input
      if (!base64Data || typeof base64Data !== "string") {
        console.error("❌ [Image Upload] Invalid base64Data input:", {
          type: typeof base64Data,
          isNull: base64Data === null,
          isUndefined: base64Data === undefined,
          value: base64Data,
        });
        throw new Error("base64Data is required and must be a string");
      }

      // Clean and validate base64 string
      let cleanBase64 = base64Data.trim();

      // Check if empty after trimming
      if (!cleanBase64) {
        console.error("❌ [Image Upload] Empty base64 string after trimming");
        throw new Error("base64 string is empty");
      }

      // Remove data URL prefix if present (e.g., "data:image/png;base64,")
      if (cleanBase64.includes(",")) {
        cleanBase64 = cleanBase64.split(",")[1];
        // Trim again after splitting
        cleanBase64 = cleanBase64.trim();
      }

      // Remove whitespace and newlines
      cleanBase64 = cleanBase64.replace(/\s/g, "");

      // Final check for empty
      if (!cleanBase64) {
        console.error("❌ [Image Upload] Empty base64 string after cleaning");
        throw new Error("base64 string is empty after cleaning");
      }

      // Log base64 details for debugging
      console.log("🔍 [Image Upload] Base64 validation check:", {
        length: cleanBase64.length,
        firstChars: cleanBase64.substring(0, 100),
        lastChars: cleanBase64.substring(Math.max(0, cleanBase64.length - 20)),
        hasDataPrefix: base64Data.includes(","),
        originalLength: base64Data.length,
        isValidLength: cleanBase64.length > 0,
        hasInvalidChars: /[^A-Za-z0-9+/=]/.test(cleanBase64),
      });

      // More lenient base64 validation - just check for basic characters
      // Base64 can have padding (=) at the end, and should only contain A-Z, a-z, 0-9, +, /
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(cleanBase64)) {
        // Find invalid characters
        const invalidChars = cleanBase64.match(/[^A-Za-z0-9+/=]/g);
        console.error("❌ [Image Upload] Invalid base64 string format:", {
          length: cleanBase64.length,
          firstChars: cleanBase64.substring(0, 100),
          lastChars: cleanBase64.substring(
            Math.max(0, cleanBase64.length - 20)
          ),
          invalidChars: invalidChars ? [...new Set(invalidChars)] : [],
          hasDataPrefix: base64Data.includes(","),
          originalLength: base64Data.length,
        });
        throw new Error(
          `Invalid base64 string format${invalidChars ? ` (invalid chars: ${[...new Set(invalidChars)].join(", ")})` : ""}`
        );
      }

      // Additional check: base64 length should be multiple of 4 (after padding)
      const paddingLength = (cleanBase64.match(/=/g) || []).length;
      const baseLength = cleanBase64.length - paddingLength;
      if (baseLength % 4 !== 0 && paddingLength === 0) {
        console.warn("⚠️ [Image Upload] Base64 length may be invalid:", {
          totalLength: cleanBase64.length,
          baseLength,
          paddingLength,
        });
        // Don't throw - let atob() handle it
      }

      let byteCharacters;
      try {
        byteCharacters = atob(cleanBase64);
      } catch (atobError) {
        console.error("❌ [Image Upload] atob() decoding failed:", {
          error: atobError.message,
          base64Length: cleanBase64.length,
          firstChars: cleanBase64.substring(0, 100),
          lastChars: cleanBase64.substring(
            Math.max(0, cleanBase64.length - 20)
          ),
          paddingChars: (cleanBase64.match(/=/g) || []).length,
        });
        throw new Error(`Failed to decode base64: ${atobError.message}`);
      }
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: "image/png" });

      const folderUuid = crypto.randomUUID();
      const fileName = `image-${Date.now()}.png`;
      // Organize by user ID: {userId}/{folderUuid}/{fileName}
      const filePath = `${userId}/${folderUuid}/${fileName}`;

      const { data, error } = await supabase.storage
        .from("chat")
        .upload(filePath, blob, {
          contentType: "image/png",
          upsert: false,
          metadata: {
            user_id: userId,
            prompt: prompt,
            generated_at: new Date().toISOString(),
            type: "ai_generated_image",
          },
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from("chat")
        .getPublicUrl(filePath);

      if (attempt > 0) {
        console.log(
          `✅ [Image Upload] Successfully uploaded to Supabase after ${attempt} retries`
        );
      }

      return {
        url: publicUrlData.publicUrl,
        path: filePath,
        bucket: "chat",
        fileName: fileName,
        folderUuid: folderUuid,
        userId: userId,
        prompt: prompt,
      };
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries) {
        const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
        console.warn(
          `⚠️ [Image Upload] Failed to upload to Supabase (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`,
          error.message
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  console.error(
    `❌ [Image Upload] Failed to upload to Supabase after ${maxRetries + 1} attempts:`,
    lastError
  );
  throw new Error(
    `Failed to upload image to Supabase after ${maxRetries + 1} attempts: ${lastError.message}`
  );
};

// Validate that a URL is a Supabase storage URL
const isSupabaseStorageUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  // Check if URL contains Supabase storage path patterns
  const supabasePatterns = [
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  ];

  // Get Supabase URL from environment
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // If supabaseUrl configured, check domain match
  if (supabaseUrl) {
    try {
      const urlObj = new URL(url);
      const supabaseUrlObj = new URL(supabaseUrl);
      if (urlObj.hostname !== supabaseUrlObj.hostname) {
        console.warn(
          "⚠️ [Image Validation] URL does not match Supabase domain:",
          {
            url: url.substring(0, 100),
            expected: supabaseUrlObj.hostname,
            actual: urlObj.hostname,
          }
        );
        return false;
      }
    } catch (e) {
      console.error("❌ [Image Validation] Invalid URL format:", e.message);
      return false;
    }
  }

  const isValid = supabasePatterns.some((pattern) => url.includes(pattern));
  if (!isValid) {
    console.warn(
      "⚠️ [Image Validation] URL does not match Supabase storage patterns:",
      {
        url: url.substring(0, 100),
        patterns: supabasePatterns,
      }
    );
  }

  return isValid;
};

const generateImageName = (prompt, providedName) => {
  if (providedName && providedName.trim()) {
    return providedName.trim();
  }

  // Generate a name from the prompt
  if (prompt && prompt.trim()) {
    // Take first few words and clean them up
    const words = prompt.trim().split(/\s+/).slice(0, 4);
    const name = words
      .join(" ")
      .replace(/[^\w\s-]/g, "") // Remove special characters except hyphens
      .replace(/\s+/g, " ") // Normalize whitespace
      .trim();

    return name || "Generated Image";
  }

  return "Generated Image";
};

export class ImageGenTool extends BaseTool {
  constructor() {
    super({
      id: "image_gen",
      name: "Image Generation",
      description:
        "Generate images using AI image generation models. Can create detailed, high-quality images from text descriptions.",
      category: "Image Generation",
      icon: "🎨",
      runInstructions:
        "Generate high-quality images from text descriptions using AI",
      definition: {
        type: "function",
        function: {
          name: "image_gen",
          description:
            "Generate images using AI image generation models. Can create detailed, high-quality images from text descriptions.",
          parameters: {
            type: "object",
            properties: {
              prompt: {
                type: "string",
                description:
                  "A detailed description of the image to generate. Be specific about style, composition, colors, and mood. CRITICAL: Pass the user's prompt EXACTLY as they provided it - do NOT modify, sanitize, rewrite, or filter any content. The prompt must be passed verbatim to preserve the user's intent.",
              },
              name: {
                type: "string",
                description: "Name or title for the generated image",
              },
            },
            required: ["prompt", "name"],
          },
        },
      },
      execute: () => {},
      type: "function",
      options: {
        rateLimit: {
          maxRequests: 5,
          perMinute: true,
        },
        streamable: false,
        requiresAuth: true,
        timeout: 60000, // 60 seconds for image generation
      },
    });

    // Override the execute method after super()
    this.execute = this.executeImageGen.bind(this);
  }

  async executeImageGen(params) {
    try {
      const { prompt, name } = params;

      if (!prompt) {
        throw new Error("Prompt is required for image generation");
      }

      // Get selected model and parameters from store
      const { selectedModel, getSelectedModel, getCurrentModelParameters } =
        useImageModelStore.getState();

      const model = getSelectedModel();
      const modelParams = getCurrentModelParameters();

      if (!model) {
        throw new Error("No image model selected");
      }

      // Log image generation request
      console.log("🎨 [Image Generation] Request initiated:", {
        prompt: prompt.substring(0, 100) + (prompt.length > 100 ? "..." : ""),
        promptLength: prompt.length,
        name: name || "auto-generated",
        model: selectedModel,
        provider: model.provider,
        parameters: modelParams,
        timestamp: new Date().toISOString(),
      });

      // Apply rate limiting
      await rateLimit("image_generation", {
        maxRequests: 5,
        perMinute: true,
      });

      // Generate image using the backend service with selected model
      const result = await chatCompletionService.generateImage({
        prompt,
        model: selectedModel,
        provider: model.provider,
        ...modelParams,
      });

      console.log("📦 [Image Generation] Backend result received:", {
        hasResult: !!result,
        hasData: !!result?.data,
        dataLength: result?.data?.length || 0,
        firstItemKeys: result?.data?.[0] ? Object.keys(result.data[0]) : [],
        hasB64Json: !!result?.data?.[0]?.b64_json,
        hasUrl: !!result?.data?.[0]?.url,
        model: selectedModel,
        timestamp: new Date().toISOString(),
      });

      // Get all images from result (supports multi-image generation)
      const allImages = result?.data || [];
      if (allImages.length === 0) {
        throw new Error("No image data returned from generation");
      }

      // Preserve has_nsfw_concepts from backend response if present
      const hasNsfwConcepts =
        result.has_nsfw_concepts || allImages[0].has_nsfw_concepts;

      // Validate that all images have b64_json
      const validImages = allImages.filter((img) => img.b64_json);
      if (validImages.length === 0) {
        console.warn(
          "⚠️ [Image Generation] Backend returned URLs instead of base64:",
          {
            imageCount: allImages.length,
            timestamp: new Date().toISOString(),
          }
        );
        throw new Error(
          "No image data (b64_json) returned from generation. " +
            "This should be handled by the backend."
        );
      }

      console.log("📤 [Image Generation] Preparing to upload images:", {
        totalImages: validImages.length,
        timestamp: new Date().toISOString(),
      });

      // Upload all images in parallel
      const uploadPromises = validImages.map((image, index) =>
        uploadImageToStorage(image.b64_json, prompt)
          .then((storageData) => ({
            ...storageData,
            index,
            revised_prompt: image.revised_prompt || prompt,
          }))
          .catch((error) => {
            console.error(`❌ Failed to upload image ${index + 1}:`, error);
            return null;
          })
      );

      const uploadResults = await Promise.all(uploadPromises);
      const uploadedImages = uploadResults.filter(Boolean);

      if (uploadedImages.length === 0) {
        throw new Error("All image uploads failed");
      }

      // Validate that all uploaded URLs are Supabase storage URLs
      const invalidUrls = uploadedImages.filter(
        (img) => !isSupabaseStorageUrl(img.url)
      );
      if (invalidUrls.length > 0) {
        console.error(
          "❌ [Image Generation] Some URLs are not valid Supabase URLs:",
          invalidUrls.map((img) => img.url)
        );
      }

      // Generate the image name
      const imageName = generateImageName(prompt, name);

      console.log("✅ [Image Generation] Images generated successfully:", {
        prompt: prompt.substring(0, 100) + (prompt.length > 100 ? "..." : ""),
        imageName,
        imageCount: uploadedImages.length,
        model: selectedModel,
        provider: model.provider,
        timestamp: new Date().toISOString(),
      });

      // Build images array for multi-image support
      const imagesArray = uploadedImages.map((storageData, idx) => ({
        url: storageData.url,
        name: idx === 0 ? imageName : `${imageName} (${idx + 1})`,
        prompt,
        model: selectedModel,
        provider: model.provider,
        parameters: modelParams,
        storagePath: storageData.path,
        storageBucket: storageData.bucket,
        fileName: storageData.fileName,
        revised_prompt: storageData.revised_prompt,
      }));

      return {
        success: true,
        result: {
          // NEW: Array of all images for multi-image display
          images: imagesArray,
          // BACKWARD COMPAT: Keep data for single-image access
          data: {
            url: uploadedImages[0].url,
            name: imageName,
            prompt,
            model: selectedModel,
            provider: model.provider,
            parameters: modelParams,
            storagePath: uploadedImages[0].path,
            storageBucket: uploadedImages[0].bucket,
            fileName: uploadedImages[0].fileName,
            revised_prompt: uploadedImages[0].revised_prompt || prompt,
          },
          metadata: {
            timestamp: new Date().toISOString(),
            model: selectedModel,
            provider: model.provider,
            parameters: modelParams,
            name: imageName,
            prompt,
            format: "url",
            imageCount: uploadedImages.length,
            storageDetails: {
              bucket: uploadedImages[0].bucket,
              paths: uploadedImages.map((img) => img.path),
              isStoredInSupabase: true,
            },
            // Preserve has_nsfw_concepts from backend response if present
            ...(hasNsfwConcepts !== undefined
              ? { has_nsfw_concepts: hasNsfwConcepts }
              : {}),
          },
        },
      };
    } catch (error) {
      console.error("❌ [Image Generation] Execution failed:", {
        error: error.message,
        errorType: error.errorType,
        prompt:
          params.prompt?.substring(0, 100) +
          (params.prompt?.length > 100 ? "..." : ""),
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      return {
        success: false,
        error: error.message || "Image generation failed",
        errorType: error.errorType || this.categorizeError(error),
        errorStatus: error.errorStatus,
      };
    }
  }

  /**
   * Categorize error type based on error message and properties
   * @param {Error} error - Error object
   * @returns {string} Error type category
   */
  categorizeError(error) {
    const message = error.message?.toLowerCase() || "";

    if (
      message.includes("safety") ||
      message.includes("content policy") ||
      message.includes("content filter") ||
      message.includes("rejected")
    ) {
      return "safety";
    }
    if (
      message.includes("rate limit") ||
      message.includes("429") ||
      message.includes("too many requests")
    ) {
      return "rate_limit";
    }
    if (
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("connection") ||
      message.includes("econnrefused") ||
      message.includes("fetch failed")
    ) {
      return "network";
    }
    return "image_generation";
  }
}

// Legacy export for backward compatibility
export const generateImage = async (params) => {
  const tool = new ImageGenTool();
  const result = await tool.executeImageGen(params);

  if (result.success) {
    return {
      status: "success",
      result: result.result,
    };
  } else {
    return {
      status: "error",
      error: result.error,
      result: {
        metadata: {
          timestamp: new Date().toISOString(),
          prompt: params.prompt,
        },
      },
    };
  }
};

export const imageGenTool = new ImageGenTool();

export default imageGenTool;
